import {
  IDefaultQuoteDocumentsSchema,
  INetTerms,
  IQuoteSettingsConfigSchema,
} from '@monetize/types/app';
import React from 'react';
import DefaultQuoteDocuments from './components/DefaultQuoteDocuments';
import NetTerms from './components/NetTerms';
import { QuoteAndContractExperienceConfiguration } from './components/QuoteAndContractExperienceConfiguration';
import { QuoteDiscountSettings } from './components/QuoteDiscountSettings';
import QuoteDocumentTypes from './components/QuoteDocumentTypes';
import { QuoteEsignSettings } from './components/QuoteEsignSettings';
import { QuoteProcessingSettings } from './components/QuoteProcessingSettings';
import { QuoteRateAndPriceSettings } from './components/QuoteRateAndPriceSettings';
import { QuoteSettingApprovals } from './components/QuoteSettingApprovals';
import { QuoteSettingEndAction } from './components/QuoteSettingEndAction';
import { QuoteSettingGeneral } from './components/QuoteSettingGeneral';
import {
  QUOTE_SETTING_KEYS,
  QuoteSettingSectionKey,
} from './quoteSettingsConfig';

export interface QuoteSettingsRenderProps {
  quoteSettings: IQuoteSettingsConfigSchema;
  isLoading: boolean;
  handleSave: (payload: IQuoteSettingsConfigSchema) => Promise<void>;
}

export interface QuoteSettingRegistryEntry {
  sectionKey: QuoteSettingSectionKey;
  render: (props: QuoteSettingsRenderProps) => React.ReactNode;
}

export const QUOTE_SETTINGS_RENDERING_REGISTRY: QuoteSettingRegistryEntry[] = [
  {
    sectionKey: QUOTE_SETTING_KEYS.general,
    render: ({ quoteSettings, isLoading, handleSave }) => (
      <QuoteSettingGeneral
        quoteSettings={quoteSettings}
        isLoading={isLoading}
        onSubmit={(data) => handleSave({ ...quoteSettings, ...data })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.quoteDocumentTypes,
    render: () => <QuoteDocumentTypes />,
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.quoteDocuments,
    render: ({ quoteSettings, isLoading }) => (
      <DefaultQuoteDocuments
        isLoading={isLoading}
        defaultQuoteDocuments={
          (quoteSettings?.defaultQuoteDocuments ??
            []) as IDefaultQuoteDocumentsSchema[]
        }
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.quoteEsignSettings,
    render: ({ quoteSettings, handleSave }) => (
      <QuoteEsignSettings
        quoteSettings={quoteSettings}
        onSubmit={(data) => handleSave({ ...quoteSettings, ...data })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.netTerms,
    render: ({ quoteSettings, handleSave }) => (
      <NetTerms
        netTerms={quoteSettings.netTerms as INetTerms}
        onSave={(netTerms) => handleSave({ ...quoteSettings, netTerms })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.approval,
    render: ({ quoteSettings, handleSave }) => (
      <QuoteSettingApprovals
        approvalData={{ quoteApprovalNotes: quoteSettings?.quoteApprovalNotes }}
        onSubmit={(quoteApproval) =>
          handleSave({ ...quoteSettings, ...quoteApproval })
        }
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.quoteProcessing,
    render: ({ quoteSettings, isLoading, handleSave }) => (
      <QuoteProcessingSettings
        value={{
          pauseAtAccepted: quoteSettings.pauseAtAccepted,
          allowQuoteLevelOverrideOfStartDateSource:
            quoteSettings?.allowQuoteLevelOverrideOfStartDateSource,
          defaultStartDateSource: quoteSettings?.defaultStartDateSource,
        }}
        isLoading={isLoading}
        onSubmit={(data) => handleSave({ ...quoteSettings, ...data })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.endAction,
    render: ({ quoteSettings, isLoading, handleSave }) => (
      <QuoteSettingEndAction
        value={{ contractRenewalTerms: quoteSettings?.contractRenewalTerms }}
        isLoading={isLoading}
        onSubmit={(data: any) => handleSave({ ...quoteSettings, ...data })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.discountSettings,
    render: ({ quoteSettings, isLoading, handleSave }) => (
      <QuoteDiscountSettings
        quoteSettings={quoteSettings}
        isLoading={isLoading}
        onSubmit={(data) => handleSave({ ...quoteSettings, ...data })}
      />
    ),
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.rateAndPriceSettings,
    render: () => <QuoteRateAndPriceSettings />,
  },
  {
    sectionKey: QUOTE_SETTING_KEYS.additionalSettings,
    render: () => <QuoteAndContractExperienceConfiguration />,
  },
];
